import { useCallback, useReducer } from "react";
import type {
	AccountData,
	InviteDraft,
	OnboardingMode,
	OnboardingStepId,
	ProfileData,
	ProjectDraft,
	SiteData,
} from "../types";

export const STEP_IDS: readonly OnboardingStepId[] = [
	"account",
	"profile",
	"mode",
	"structure",
	"team",
	"summary",
];

const LAST_INDEX = STEP_IDS.length - 1;

export interface OnboardingFlowData {
	account: AccountData | null;
	profile: ProfileData | null;
	mode: OnboardingMode | null;
	site: SiteData | null;
	singleProject: ProjectDraft | null;
	invites: InviteDraft[];
}

interface OnboardingState extends OnboardingFlowData {
	stepIndex: number;
}

const INITIAL_STATE: OnboardingState = {
	stepIndex: 0,
	account: null,
	profile: null,
	mode: null,
	site: null,
	singleProject: null,
	invites: [],
};

export interface OnboardingFlowInit {
	startStep?: OnboardingStepId;
	account?: AccountData | null;
}

function createInitialState(init?: OnboardingFlowInit): OnboardingState {
	const index = init?.startStep ? STEP_IDS.indexOf(init.startStep) : 0;
	return {
		...INITIAL_STATE,
		stepIndex: index >= 0 ? index : 0,
		account: init?.account ?? null,
	};
}

type Action =
	| { type: "next" }
	| { type: "back" }
	| { type: "goTo"; step: OnboardingStepId }
	| { type: "setAccount"; value: AccountData | null }
	| { type: "setProfile"; value: ProfileData }
	| { type: "setMode"; value: OnboardingMode }
	| { type: "setSite"; value: SiteData }
	| { type: "setSingleProject"; value: ProjectDraft }
	| { type: "addInvite"; value: InviteDraft }
	| { type: "removeInvite"; id: string };

function reducer(state: OnboardingState, action: Action): OnboardingState {
	switch (action.type) {
		case "next":
			return {
				...state,
				stepIndex: Math.min(state.stepIndex + 1, LAST_INDEX),
			};
		case "back":
			return { ...state, stepIndex: Math.max(state.stepIndex - 1, 0) };
		case "goTo": {
			const index = STEP_IDS.indexOf(action.step);
			return index >= 0 ? { ...state, stepIndex: index } : state;
		}
		case "setAccount":
			return { ...state, account: action.value };
		case "setProfile":
			return { ...state, profile: action.value };
		case "setMode":
			return { ...state, mode: action.value };
		case "setSite":
			return { ...state, site: action.value };
		case "setSingleProject":
			return { ...state, singleProject: action.value };
		case "addInvite":
			return { ...state, invites: [...state.invites, action.value] };
		case "removeInvite":
			return {
				...state,
				invites: state.invites.filter((invite) => invite.id !== action.id),
			};
		default:
			return state;
	}
}

export function useOnboardingFlow(init?: OnboardingFlowInit) {
	const [state, dispatch] = useReducer(reducer, init, createInitialState);

	return {
		step: STEP_IDS[state.stepIndex] ?? "account",
		stepIndex: state.stepIndex,
		data: state as OnboardingFlowData,
		next: useCallback(() => dispatch({ type: "next" }), []),
		back: useCallback(() => dispatch({ type: "back" }), []),
		goTo: useCallback(
			(step: OnboardingStepId) => dispatch({ type: "goTo", step }),
			[],
		),
		setAccount: useCallback(
			(value: AccountData | null) => dispatch({ type: "setAccount", value }),
			[],
		),
		setProfile: useCallback(
			(value: ProfileData) => dispatch({ type: "setProfile", value }),
			[],
		),
		setMode: useCallback(
			(value: OnboardingMode) => dispatch({ type: "setMode", value }),
			[],
		),
		setSite: useCallback(
			(value: SiteData) => dispatch({ type: "setSite", value }),
			[],
		),
		setSingleProject: useCallback(
			(value: ProjectDraft) => dispatch({ type: "setSingleProject", value }),
			[],
		),
		addInvite: useCallback(
			(value: InviteDraft) => dispatch({ type: "addInvite", value }),
			[],
		),
		removeInvite: useCallback(
			(id: string) => dispatch({ type: "removeInvite", id }),
			[],
		),
	};
}
